import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { compressImageToDataURL } from "@/lib/image";
import { Upload, Download, Search, ImageIcon as ImageIco } from "lucide-react";

export default function MemberGallery() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("approved");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", image_url: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    if (tab === "mine") {
      const { data } = await api.get("/gallery", { params: { q: q || undefined } });
      setItems((data || []).filter((g) => g.uploader_id === user?.id));
    } else {
      const { data } = await api.get("/gallery", { params: { status: "approved", q: q || undefined } });
      setItems(data || []);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, tab]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Max 15MB before compression"); return; }
    // Gallery is browsed at ≤4-across grid — 1600px longest edge keeps
    // full-screen viewing crisp while stripping camera-original bloat.
    const dataUrl = await compressImageToDataURL(f, { maxDim: 1600 });
    setForm((s) => ({ ...s, image_url: dataUrl }));
  };

  const upload = async () => {
    if (!form.image_url) { toast.error("Pick an image"); return; }
    setBusy(true);
    try {
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      await api.post("/gallery", { ...form, tags });
      toast.success("Uploaded — awaiting admin approval");
      setOpen(false);
      setForm({ title: "", description: "", image_url: "", tags: "" });
      load();
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const download = async (item) => {
    try {
      const a = document.createElement("a");
      a.href = item.image_url;
      a.download = (item.title || "photo") + ".jpg";
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { toast.error("Download failed"); }
  };

  return (
    <AppShell variant="member" title="Photo gallery" actions={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="btn-primary-modern h-10" data-testid="gallery-upload-open"><Upload className="h-4 w-4 mr-2" /> Upload</Button>
        </DialogTrigger>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Upload a photo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Photo</Label>
              <div className="mt-1.5 border border-dashed border-border rounded-xl p-5 text-center bg-[hsl(var(--secondary))]/40">
                <input ref={fileRef} onChange={onFile} type="file" accept="image/png,.png" className="hidden" data-testid="gallery-file-input" />
                {form.image_url ? <img src={form.image_url} alt="preview" className="max-h-48 mx-auto rounded-lg" /> :
                  <div className="py-6"><ImageIco className="h-8 w-8 mx-auto text-muted-foreground mb-2" /><div className="text-sm text-muted-foreground">Click to choose an image · auto-shrunk to 1600px</div></div>}
                <Button type="button" variant="outline" size="sm" className="mt-3 rounded-full" onClick={() => fileRef.current?.click()}>Choose file</Button>
              </div>
            </div>
            <div><Label className="text-sm font-medium">Title</Label><Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} className="mt-1.5 h-11 rounded-lg" data-testid="gallery-title" /></div>
            <div><Label className="text-sm font-medium">Description</Label><Textarea value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} rows={2} className="mt-1.5 rounded-lg" /></div>
            <div><Label className="text-sm font-medium">Tags <span className="text-muted-foreground font-normal">(comma separated)</span></Label><Input value={form.tags} onChange={(e) => setForm((s) => ({ ...s, tags: e.target.value }))} placeholder="match, training, juniors" className="mt-1.5 h-11 rounded-lg" /></div>
            <Button disabled={busy} onClick={upload} className="w-full h-11 btn-primary-modern" data-testid="gallery-upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    }>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search photos…" className="pl-10 h-11 rounded-full bg-[hsl(var(--card))]" data-testid="gallery-search" />
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-full">
            <TabsTrigger value="approved" className="rounded-full" data-testid="gallery-tab-approved">All</TabsTrigger>
            <TabsTrigger value="mine" className="rounded-full" data-testid="gallery-tab-mine">My uploads</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 text-center text-muted-foreground border-dashed rounded-2xl">
          <ImageIco className="mx-auto h-8 w-8 mb-3" /> No photos yet.
        </Card>
      ) : (
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 [column-fill:_balance]" data-testid="gallery-grid">
          {items.map((g) => (
            <Card key={g.id} className="mb-3 break-inside-avoid overflow-hidden group relative rounded-2xl border-border hover:border-foreground/40 transition-all">
              <div className="bg-[hsl(var(--secondary))] overflow-hidden">
                <img src={g.image_url} alt={g.title || "Photo"} className="w-full h-auto block group-hover:scale-105 transition-transform duration-500" />
              </div>
              {g.status !== "approved" && <Badge variant="outline" className="absolute top-2 left-2 bg-[hsl(var(--card))]/90 backdrop-blur">{g.status}</Badge>}
              <div className="p-3">
                <div className="text-sm font-medium truncate">{g.title || "Untitled"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{g.uploader_name}</div>
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" className="rounded-full h-9 w-9 btn-accent-modern" onClick={() => download(g)} data-testid={`gallery-download-${g.id}`}><Download className="h-4 w-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
